import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactInfo } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactInfo;
}

const CONTACT_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'email', formField: 'primaryEmail' },
  { apiField: 'emailStatus', formField: 'emailStatus' },
  { apiField: 'emailAddress2', formField: 'emailAddress2' },
  { apiField: 'emailAddress3', formField: 'emailAddress3' },
  { apiField: 'mobile', formField: 'mobilePhone' },
  { apiField: 'companyPhone', formField: 'companyPhone' },
  { apiField: 'assistantPhone', formField: 'assistantPhone' },
  { apiField: 'portalUrl', formField: 'portalUrl' },
  { apiField: 'ftpSite', formField: 'ftpSite' },
  { apiField: 'website', formField: 'website' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactInfo,
  initial: ContactInfo
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        email
        emailStatus
        emailAddress2
        emailAddress3
        mobile
        companyPhone
        assistantPhone
        portalUrl
        ftpSite
        website
      }
    }
  }
`;

export interface EditContactInfoDialogProps {
  contactId: string;
  initialValue: ContactInfo;
  isOpen: boolean;
  onChange: (data: ContactInfo) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactInfo;
}

export function EditContactInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditContactInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(CONTACT_INFO_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactInfo>(
    field: K,
    fieldValue: ContactInfo[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save contact information');
      } else {
        toast.success('Contact information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving contact information:', error);
      toast.error('Failed to save contact information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Contact Information"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Primary Email"
          onChange={primaryEmail => {
            updateField('primaryEmail', primaryEmail);
          }}
          type="email"
          value={value.primaryEmail}
        />
        <EditDynamicSelectField
          fieldName="email_status"
          label="Email Status"
          model="res.partner"
          onChange={v => {
            updateField('emailStatus', v);
          }}
          value={value.emailStatus}
        />
        <EditFormField
          label="Email Address 2"
          onChange={emailAddress2 => {
            updateField('emailAddress2', emailAddress2);
          }}
          type="email"
          value={value.emailAddress2}
        />
        <EditFormField
          label="Email Address 3"
          onChange={emailAddress3 => {
            updateField('emailAddress3', emailAddress3);
          }}
          type="email"
          value={value.emailAddress3}
        />
        <EditFormField
          label="Mobile Phone"
          onChange={mobilePhone => {
            updateField('mobilePhone', mobilePhone);
          }}
          type="tel"
          value={value.mobilePhone}
        />
        <EditFormField
          label="Company Phone"
          onChange={companyPhone => {
            updateField('companyPhone', companyPhone);
          }}
          type="tel"
          value={value.companyPhone}
        />
        <EditFormField
          label="Assistant Phone"
          onChange={assistantPhone => {
            updateField('assistantPhone', assistantPhone);
          }}
          type="tel"
          value={value.assistantPhone}
        />
        <EditFormField
          label="Portal URL"
          onChange={portalUrl => {
            updateField('portalUrl', portalUrl);
          }}
          type="url"
          value={value.portalUrl}
        />
        <EditFormField
          label="FTP Site"
          onChange={ftpSite => {
            updateField('ftpSite', ftpSite);
          }}
          type="url"
          value={value.ftpSite}
        />
        <EditFormField
          label="Website"
          onChange={website => {
            updateField('website', website);
          }}
          type="url"
          value={value.website}
        />
      </div>
    </EditDialog>
  );
}
